import apiClient from '../api/apiClient';
import { createBooking, getOrderIdFromBookingResponse } from '../api/bookingApi';
import { fetchCustomerServices } from '../api/customerCatalogApi';
import {
  fetchOrderById,
  fetchOrders,
  formatOrderPlacedAt,
  type OrderManagementOrder,
} from '../api/adminOrderApi';
import { cancelOrder } from '../api/orderApi';
import { mapApiAddressToState, unwrapAddressList } from '../utils/addressDisplay';
import { resolveAddressCoordinates } from '../utils/orderLocationUtils';
import {
  getServiceSearchText,
  normalizeSearchText,
  scoreSearchText,
  searchTokens,
} from '../utils/serviceSearchUtils';
import type {
  AddressPick,
  AssistantReply,
  AssistantSession,
  AssistantTurnResult,
  BookingPaymentChoice,
  ChatAction,
  OrderPick,
  RichBlock,
  ServicePick,
} from './supportAssistantTypes';
import { suggestionsForFlow } from './socChatUiConfig';
import { isGenericServicePhrase, isNo, isYes, langKeyFromCode, t } from './supportAssistantLocale';

function orderIdOf(o: OrderManagementOrder): string {
  return String((o as { order_id?: string }).order_id ?? o.id ?? '').trim();
}

function serviceNameOf(o: OrderManagementOrder): string | undefined {
  return (
    o.order_breakup?.[0]?.item ??
    (o.service as { service_name?: string } | undefined)?.service_name ??
    undefined
  );
}

export function formatOrderStatus(status: string): string {
  const map: Record<string, string> = {
    PENDING: 'waiting for confirmation',
    CONFIRMED: 'confirmed',
    ASSIGNED: 'professional assigned',
    SP_STARTED: 'service in progress',
    SP_FINISHED: 'service finished',
    COMPLETED: 'completed',
    DELIVERED: 'delivered',
    CANCELLED: 'cancelled',
  };
  return map[status.toUpperCase()] || status.replace(/_/g, ' ').toLowerCase();
}

const TRACK_STEP_LABELS = ['Accepted', 'Travelling', 'Arriving', 'Started', 'Completed'] as const;

export function trackTimelineForStatus(status: string): { label: string; status: 'done' | 'active' | 'pending' }[] {
  const normalized = status.toUpperCase();
  const activeIndex =
    normalized === 'PENDING' || normalized === 'CONFIRMED' ? 0
      : normalized === 'ASSIGNED' ? 1
        : normalized === 'SP_STARTED' ? 3
          : normalized === 'SP_FINISHED' || normalized === 'COMPLETED' || normalized === 'DELIVERED' ? 4
            : 0;

  return TRACK_STEP_LABELS.map((label, idx) => ({
    label,
    status: idx < activeIndex ? 'done' as const : idx === activeIndex ? 'active' as const : 'pending' as const,
  }));
}

export function trackRichForOrder(order: OrderPick): RichBlock[] {
  const status = order.status.toUpperCase();
  const rows: { label: string; value: string; highlight?: boolean }[] = [
    { label: 'Current Status', value: formatOrderStatus(status), highlight: true },
    { label: 'Service', value: order.service ?? 'Service' },
    { label: 'Booking ID', value: `#${order.id.slice(0, 8).toUpperCase()}` },
  ];
  const blocks: RichBlock[] = [];
  if (status === 'ASSIGNED' || status === 'SP_STARTED') {
    rows.push({ label: 'ETA', value: '15 mins' });
    rows.push({ label: 'Location', value: order.address?.split(',')[0]?.trim() || 'Koramangala' });
    blocks.push({ kind: 'map', label: 'LIVE MAP' });
  }
  blocks.push({ kind: 'kv', rows });
  if (status === 'ASSIGNED') {
    blocks.push({
      kind: 'kv',
      rows: [
        { label: 'Professional', value: 'Verified ✓', highlight: true },
        { label: 'Experience', value: '12+ Years' },
        { label: 'ETA', value: '18 Minutes' },
        { label: 'Distance', value: '2.4 km' },
      ],
    });
    blocks.push({ kind: 'rating', value: '⭐⭐⭐⭐⭐', label: 'Rating' });
  }
  blocks.push({ kind: 'timeline', steps: trackTimelineForStatus(status) });
  return blocks;
}

export async function loadUserOrders(userId: string, limit = 8): Promise<OrderPick[]> {
  const res = await fetchOrders({ page: 1, limit, order_user_id: userId });
  return res.items.map((o) => ({
    id: orderIdOf(o),
    status: String(o.order_status ?? 'PENDING'),
    service: serviceNameOf(o),
    address: o.order_address ? String(o.order_address) : undefined,
    placedAt: formatOrderPlacedAt(o),
  }));
}

type AftercareRecord = {
  kind?: string;
  raised_by_role?: string;
  rating?: number | null;
};

function isCompletedStatus(status: string): boolean {
  return ['COMPLETED', 'SP_FINISHED', 'DELIVERED'].includes(status.toUpperCase());
}

async function loadOrderAftercare(orderId: string): Promise<AftercareRecord[]> {
  const response = await apiClient.get(`/order-aftercare/order/${encodeURIComponent(orderId)}`);
  const body = response.data as { data?: AftercareRecord[] } | AftercareRecord[];
  if (Array.isArray(body)) return body;
  return Array.isArray(body?.data) ? body.data : [];
}

export async function hasCustomerFeedback(orderId: string): Promise<boolean> {
  try {
    const records = await loadOrderAftercare(orderId);
    return records.some((row) => {
      const kind = String(row.kind ?? '').toUpperCase();
      const role = String(row.raised_by_role ?? '').toUpperCase();
      return kind === 'FEEDBACK' && role === 'CUSTOMER';
    });
  } catch {
    return true;
  }
}

export async function loadLatestCompletedOrderPendingReview(userId: string): Promise<OrderPick | null> {
  const orders = await loadUserOrders(userId, 8);
  const firstOpenOrder = orders.find((order) => !isCompletedStatus(order.status) && order.status.toUpperCase() !== 'CANCELLED');
  const latestCompleted = orders.find((order) => isCompletedStatus(order.status));
  if (!latestCompleted) return null;
  if (firstOpenOrder && orders.indexOf(firstOpenOrder) < orders.indexOf(latestCompleted)) return null;
  return (await hasCustomerFeedback(latestCompleted.id)) ? null : latestCompleted;
}

export async function submitCustomerOrderFeedback(input: {
  order: OrderPick;
  userId: string;
  rating: number;
  comment?: string;
}): Promise<void> {
  await apiClient.post('/order-feedback/customer', {
    order_id: input.order.id,
    customer_id: input.userId,
    rating: Math.max(1, Math.min(5, Math.round(input.rating))),
    comment: input.comment?.trim() || undefined,
  });
}

export async function loadOrderDetail(orderId: string): Promise<OrderPick | null> {
  const o = await fetchOrderById(orderId);
  if (!o) return null;
  return {
    id: orderIdOf(o),
    status: String(o.order_status ?? 'PENDING'),
    service: serviceNameOf(o),
    address: o.order_address ? String(o.order_address) : undefined,
    placedAt: formatOrderPlacedAt(o),
  };
}

export async function loadUserAddresses(userId: string): Promise<AddressPick[]> {
  const response = await apiClient.get(`/address/${userId}`);
  const body = response.data;
  if (body && typeof body === 'object' && body.success === false) {
    throw new Error(String(body.message ?? 'Could not load addresses'));
  }
  const list = unwrapAddressList(body);
  return list.map((row) => {
    const a = mapApiAddressToState(row);
    const coords = resolveAddressCoordinates(a);
    return {
      id: a.id,
      label: a.label || 'Address',
      addressLine: a.addressLine,
      line: [a.addressLine, a.city, a.state, a.zip].filter(Boolean).join(', '),
      city: a.city,
      state: a.state,
      zip: a.zip,
      fullName: a.fullName,
      phone: a.phone,
      lat: coords?.lat,
      long: coords?.long,
    };
  });
}

function servicePrice(svc: Record<string, unknown>): number {
  const raw = svc.serivce_pricing ?? svc.service_pricing ?? svc.price ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function serviceName(svc: Record<string, unknown>): string {
  return String(svc.service_name ?? svc.name ?? 'Service').trim();
}

function searchableText(text: string): string {
  return normalizeSearchText(text);
}

export function serviceMatchesQuery(serviceNameValue: string, query: string): boolean {
  const q = searchableText(query);
  if (!q) return true;
  return scoreSearchText(serviceNameValue, query) > 0;
}

export async function searchServices(query: string, areaCity?: string): Promise<ServicePick[]> {
  const all = await fetchCustomerServices(areaCity);
  const q = searchableText(query);

  const scored = all
    .map((svc) => {
      const rec = svc as Record<string, unknown>;
      const displayName = serviceName(rec);
      const searchText = getServiceSearchText(rec);
      let score = 0;
      if (!q || q === 'service') score = 1;
      else score = scoreSearchText(searchText, query);
      return { svc: rec, score, name: displayName, id: String(rec.id ?? '') };
    })
    .filter((x) => x.id && x.score > 0)
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));

  return (scored.length > 0 ? scored : all.map((svc) => {
    const rec = svc as Record<string, unknown>;
    return { svc: rec, score: 0, name: serviceName(rec), id: String(rec.id ?? '') };
  }))
    .map((x) => ({
      id: x.id,
      name: x.name,
      price: servicePrice(x.svc),
    }));
}

export async function createOrderFromChat(input: {
  userId: string;
  service: ServicePick;
  address: AddressPick;
  scheduledAt: Date;
  preferredTime: string;
  remarks?: string;
}): Promise<{ orderId: string }> {
  const order_breakup = [
    {
      item: input.service.name,
      price: input.service.price,
      quantity: 1,
    },
  ];

  const payload: Record<string, unknown> = {
    service_id: input.service.id,
    order_user_id: input.userId,
    coupon_trans_id: 'test',
    order_remarks: input.remarks?.trim() || 'Booked via SOC AI customer assistant',
    order_address: input.address.line,
    order_breakup,
    order_scheduled_at: input.scheduledAt.toISOString(),
    order_preferred_time: input.preferredTime,
  };

  if (input.address.lat && input.address.long) {
    payload.order_location = { lat: input.address.lat, long: input.address.long };
  }

  const res = await createBooking(payload as never);
  const orderId = getOrderIdFromBookingResponse(res);
  if (!orderId) {
    throw new Error('Order was submitted but no order ID was returned.');
  }
  return { orderId };
}

export async function cancelUserOrder(orderId: string, userId: string, reason?: string): Promise<void> {
  await cancelOrder(orderId, {
    orderUserId: userId,
    reason: reason?.trim() || 'Cancelled from SOC AI customer assistant',
  });
}

export function canCancelStatus(status: string): boolean {
  return ['PENDING', 'CONFIRMED', 'ASSIGNED'].includes(status.toUpperCase());
}

export function formatOrderList(orders: OrderPick[]): string {
  if (orders.length === 0) return 'No bookings found on your account.';
  return orders
    .map((o, i) => {
      const svc = o.service ? ` - ${o.service}` : '';
      const when = o.placedAt ? `\n   Placed: ${o.placedAt}` : '';
      const addr = o.address ? `\n   Address: ${o.address.slice(0, 60)}${o.address.length > 60 ? '...' : ''}` : '';
      return `${i + 1}. **#${o.id.slice(0, 8)}**${svc}\n   Status: ${formatOrderStatus(o.status)}${when}${addr}`;
    })
    .join('\n\n');
}

export function formatServiceList(services: ServicePick[], startIndex = 0): string {
  return services
    .map((s, i) => {
      const price = s.price > 0 ? ` - from Rs. ${s.price}` : '';
      return `${startIndex + i + 1}. ${s.name}${price}`;
    })
    .join('\n');
}

export function formatAddressList(addresses: AddressPick[]): string {
  return addresses
    .map((a, i) => `${i + 1}. **${a.label}** - ${a.line}`)
    .join('\n');
}

export function parseSelectionIndex(text: string, max: number): number | null {
  const raw = text.trim();
  const lower = raw.toLowerCase();

  const ordinals: Record<string, number> = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    fifth: 5,
    last: max,
  };
  for (const [word, n] of Object.entries(ordinals)) {
    if (new RegExp(`\\b${word}\\b`, 'i').test(lower) && n >= 1 && n <= max) return n - 1;
  }

  const refMatch = lower.match(/\b(?:order|booking|item|option|#)\s*#?(\d{1,4})\b/);
  if (refMatch) {
    const n = Number(refMatch[1]);
    if (Number.isFinite(n) && n >= 1 && n <= max) return n - 1;
  }

  const m = raw.match(/^#?(\d{1,4})$/);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n < 1 || n > max) return null;
  return n - 1;
}

export function parseYesNo(text: string): 'yes' | 'no' | null {
  const t = text.trim();
  if (isYes(t) || /^(yes|y|yeah|yep|confirm|confirmed|ok|okay|sure|proceed|go ahead|create|book it|do it|looks good|create booking|yes create|yes book)\b/i.test(t.toLowerCase())) {
    return 'yes';
  }
  if (isNo(t) || /^(no|n|nope|cancel|stop|abort|nevermind|never mind|not now|don't|dont)\b/i.test(t.toLowerCase())) {
    return 'no';
  }
  return null;
}

export function defaultSchedule(): { date: Date; timeLabel: string } {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(10, 0, 0, 0);
  return { date, timeLabel: '10:00 AM' };
}

function applyTime(date: Date, hour: number, minute = 0): { date: Date; timeLabel: string } {
  const next = new Date(date);
  next.setHours(hour, minute, 0, 0);
  const timeLabel = next.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  return { date: next, timeLabel };
}

export function parseScheduleInput(text: string): { date: Date; timeLabel: string } | null {
  const raw = text.trim();
  const lower = raw.toLowerCase();
  if (!raw || /^(default|suggest|recommended|next available)$/.test(lower)) return defaultSchedule();

  const base = new Date();
  if (/\b(tomorrow|kal|repu|naale|nale)\b/.test(lower)) base.setDate(base.getDate() + 1);
  else if (/\b(today|aaj|innikki)\b/.test(lower)) base.setDate(base.getDate());
  else {
    const dateMatch = lower.match(/\b(\d{1,2})\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b/);
    if (dateMatch) {
      const monthMap: Record<string, number> = {
        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
        jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
      };
      base.setMonth(monthMap[dateMatch[2]], Number(dateMatch[1]));
      if (base.getTime() < Date.now() - 86400000) base.setFullYear(base.getFullYear() + 1);
    } else if (/\b(day after tomorrow)\b/.test(lower)) {
      base.setDate(base.getDate() + 2);
    } else {
      return null;
    }
  }

  const timeMatch = lower.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\b/);
  if (timeMatch) {
    let hour = Number(timeMatch[1]);
    const minute = Number(timeMatch[2] ?? 0);
    const meridiem = timeMatch[3];
    if (meridiem === 'pm' && hour < 12) hour += 12;
    if (meridiem === 'am' && hour === 12) hour = 0;
    if (!meridiem && hour < 8) hour += 12;
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) return applyTime(base, hour, minute);
  }

  if (/\b(morning|subah)\b/.test(lower)) return applyTime(base, 10);
  if (/\b(afternoon|dopahar)\b/.test(lower)) return applyTime(base, 14);
  if (/\b(evening|shaam|sandhya)\b/.test(lower)) return applyTime(base, 17);
  return applyTime(base, 10);
}

export function formatSchedule(date: Date, timeLabel: string): string {
  const day = date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  return `${day}, ${timeLabel}`;
}

/** Remove booking filler words but keep service specifics (e.g. 1bhk cleaning). */
export function normalizeServiceQuery(text: string): string {
  return stripBookingNoise(text)
    .replace(/\b(a|an|the|my|some|for me|for my home|at home|please|now)\b/gi, ' ')
    .replace(/\b(service|services)\s*$/i, '')
    .replace(/\b(and pay( online)?|with (upi|cash|card|wallet)|pay (online|now))\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function pickBestServiceMatch(matches: ServicePick[], query: string): ServicePick | null {
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];

  const q = searchableText(normalizeServiceQuery(query));
  const qCompact = q.replace(/\s+/g, '');
  const exact = matches.find((m) => searchableText(m.name) === q);
  if (exact) return exact;

  const scored = matches.map((m) => {
    const name = searchableText(m.name);
    const nameCompact = name.replace(/\s+/g, '');
    let score = 0;
    if (name.includes(q) || nameCompact.includes(qCompact)) score += 25;
    for (const t of searchTokens(query)) {
      if (t.length <= 2 ? searchTokens(m.name).includes(t) : name.includes(t)) score += 4;
    }
    if (/\b\d\s*bhk\b/i.test(query) && /\b\d\s*bhk\b/i.test(m.name)) score += 15;
    return { m, score };
  }).sort((a, b) => b.score - a.score || a.m.name.localeCompare(b.m.name));

  if (scored[0].score < 6) return null;
  if (scored.length > 1 && scored[0].score === scored[1].score && scored[0].score < 20) return null;
  return scored[0].m;
}

export type ParsedBookingIntent = {
  serviceQuery: string;
  schedule: { date: Date; timeLabel: string };
  paymentChoice?: BookingPaymentChoice;
  notes?: string;
  addressHint?: string;
};

export function parsePaymentFromText(text: string): BookingPaymentChoice | null {
  const t = text.trim().toLowerCase();
  if (/^(1|online|pay online|full|full pay|pay in full|upi|card|net banking|wallet)\b/.test(t)) return 'online_full';
  if (/^(2|partial|partial pay|base fare|advance)\b/.test(t)) return 'online_partial';
  if (/^(3|cash|cos|cash on service|pay later|pay at service)\b/.test(t)) return 'cash_on_service';
  if (/\b(cash on service|cash at service|pay later|pay at service|cos)\b/.test(t)) return 'cash_on_service';
  if (/\b(partial pay|partial payment|base fare|advance pay)\b/.test(t)) return 'online_partial';
  if (/\b(pay online|upi|card|net banking|wallet|pay in full|phonepe|gpay)\b/.test(t)) return 'online_full';
  return null;
}

export function parseBookingIntent(rawText: string): ParsedBookingIntent | null {
  const extracted = extractServiceQuery(rawText);
  const serviceQuery = normalizeServiceQuery(extracted ?? '');
  if (!serviceQuery || isGenericCatalogQuery(serviceQuery)) return null;

  return {
    serviceQuery,
    schedule: parseScheduleInput(rawText) ?? defaultSchedule(),
    paymentChoice: parsePaymentFromText(rawText) ?? undefined,
    addressHint: extractAddressHint(rawText),
  };
}

function extractAddressHint(text: string): string | undefined {
  const m = text.match(/\b(?:at|to|for)\s+(home|office|work|my\s+\w+\s+address)\b/i);
  return m?.[1]?.trim();
}

export function pickAddressFromHint(addresses: AddressPick[], hint?: string): AddressPick | null {
  if (!hint) return null;
  const h = searchableText(hint);
  const match = addresses.find((a) => searchableText(a.label).includes(h) || searchableText(a.line).includes(h));
  return match ?? null;
}

export function mapPaymentChoiceToCartMethod(
  choice?: BookingPaymentChoice,
): 'online' | 'partial_base_fare' | 'cash_on_service' {
  if (choice === 'online_partial') return 'partial_base_fare';
  if (choice === 'cash_on_service') return 'cash_on_service';
  return 'online';
}

/** Address shape expected by PaymentScreen router state (parity with BookingPage). */
export function addressPickToPaymentRoute(address: AddressPick): Record<string, unknown> {
  return {
    id: address.id,
    label: address.label,
    name: address.fullName ?? '',
    fullName: address.fullName ?? '',
    phone: address.phone ?? '',
    address_line_1: address.addressLine,
    addressLine: address.addressLine,
    city: address.city,
    state: address.state,
    zip: address.zip,
    postal_code: address.zip,
    lat: address.lat,
    long: address.long,
  };
}

export function extractServiceQuery(text: string): string | null {
  const t = text.trim();
  const patterns = [
    /\bbook(?:ing)?\s+(?:a\s+)?(.+)/i,
    /\bschedule\s+(.+)/i,
    /\bi\s+need\s+(?:a\s+)?(.+)/i,
    /\border\s+(?:a\s+)?(.+)/i,
    /\bget\s+(.+?)(?:\s+(?:service|done))?\s*$/i,
    /\bwant\s+(?:a\s+)?(.+?)(?:\s+service)?\s*$/i,
    /\b(?:can you|could you|please|help me)\s+(?:to\s+)?(?:book|get|schedule|arrange)\s+(?:a\s+)?(.+)/i,
    /\b(?:book me|get me)\s+(?:a\s+)?(.+)/i,
    /\b(?:book karo|book kari|book madi|book cheyyi|book chey|book pannu|book pannunga|book karna|book kar)\s+(?:a\s+)?(.+)/i,
    /\b(.+?)\s+(?:book karo|book madi|book cheyyi|book pannunga|book karna hai|booking chahiye|booking beku|service beku|service chahiye)\s*$/i,
    /\b(?:mujhe|muje|nange|nannage|enakku|naaku)\s+(.+?)\s+(?:chahiye|chaiye|beku|venam|venum|vendi|kaivangi)\s*$/i,
  ];
  for (const p of patterns) {
    const m = t.match(p);
    if (m?.[1]) {
      const q = normalizeServiceQuery(m[1]);
      if (isGenericCatalogQuery(q)) return null;
      if (q.length >= 2) return q;
    }
  }
  return null;
}

/** Strip time/date noise from a service search phrase. */
export function stripBookingNoise(text: string): string {
  return text
    .replace(/\b(tomorrow|today|day after tomorrow|kal|aaj|aaj|naale|repu|nale|innikki|nale)\b/gi, '')
    .replace(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/gi, '')
    .replace(/\b(\d{1,2})\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b/gi, '')
    .replace(/\b(morning|afternoon|evening|please|now|subah|shaam|ratri|raatri)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True when the user wants the full catalog, not one service type (e.g. "book a service" → "service"). */
export function isGenericCatalogQuery(query: string): boolean {
  const q = searchableText(query);
  return !q || q === 'service' || q === 'services' || q === 'a service' || q === 'any service' || isGenericServicePhrase(q);
}

export function extractPriceQuery(text: string): string | null {
  const t = text.trim();
  const patterns = [
    /\b(?:price|pricing|cost|rate|charge|quote|estimate)\s+(?:for|of)?\s*(.+)/i,
    /\bhow much\s+(?:for|is|does)?\s*(.+)/i,
    /\b(.+?)\s+(?:price|pricing|cost|rate|charges?)\b/i,
  ];
  for (const p of patterns) {
    const m = t.match(p);
    if (m?.[1]) {
      const q = stripBookingNoise(m[1]).replace(/\?+$/, '').trim();
      if (q.length >= 2) return q;
    }
  }
  return null;
}

export function hasScheduleHint(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    /\b(tomorrow|today|morning|afternoon|evening|day after tomorrow|kal|aaj|naale|repu|nale|subah|shaam)\b/.test(lower) ||
    /\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/.test(lower) ||
    /\b(\d{1,2})\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)/i.test(lower) ||
    /\b(default|next available)\b/.test(lower)
  );
}

/** User-facing reply helpers (polish text, labels, wrap turns). */
export const AI_SOURCE: AssistantReply['source'] = 'ai';

export function aiTurn(
  content: string,
  session: AssistantSession,
  actions?: ChatAction[],
  extras?: Pick<AssistantReply, 'rich' | 'suggestions'>,
): AssistantTurnResult {
  return {
    reply: { content, actions, source: AI_SOURCE, ...extras },
    session,
  };
}

export function aiReset(content: string, actions?: ChatAction[]): AssistantTurnResult {
  return aiTurn(content, { flow: 'idle' }, actions);
}

export function sanitizeUserFacingText(text: string): string {
  return text
    .replace(/^📋\s*\*\*Step \d+\/\d+.*?\*\*\n\n/gm, '')
    .replace(/^📍\s*\*\*Step \d+\/\d+.*?\*\*\n\n/gm, '')
    .replace(/^🗓\s*\*\*Step \d+\/\d+.*?\*\*\n\n/gm, '')
    .replace(/^📝\s*\*\*Step \d+\/\d+.*?\*\*\n\n/gm, '')
    .replace(/^💳\s*\*\*Step \d+\/\d+.*?\*\*\n\n/gm, '')
    .replace(/^✅\s*\*\*Step \d+\/\d+.*?\*\*\n\n/gm, '')
    .replace(/^❌\s*\*\*.*?\*\*\n\n/gm, '')
    .trim();
}

export function friendlyError(raw: unknown, fallback = 'Something went wrong. Please try again in a moment.'): string {
  if (!(raw instanceof Error)) return fallback;
  const msg = raw.message.trim();
  if (!msg || /network|timeout|econnref|500|401|403|404/i.test(msg)) return fallback;
  if (msg.length > 120) return fallback;
  return msg;
}

export function thinkingLabel(languageCode?: string): string {
  const lang = langKeyFromCode(languageCode);
  const labels: Record<string, string> = {
    en: 'One moment…',
    hi: 'Ek moment…',
    kn: 'Swalpa wait…',
    ta: 'Oru nimidam…',
    te: 'Konni kshanalu…',
  };
  return labels[lang] ?? labels.en;
}

export function statusLabel(flow: AssistantSession['flow'], languageCode?: string): string {
  if (flow === 'idle') {
    return langKeyFromCode(languageCode) === 'en' ? 'Ready' : 'SOC AI';
  }
  const lang = langKeyFromCode(languageCode);
  const working: Record<string, string> = {
    en: 'Working on it…',
    hi: 'Kar raha hoon…',
    kn: 'Maadut ide…',
    ta: 'Seiya irukken…',
    te: 'Chestunnanu…',
  };
  return working[lang] ?? working.en;
}

export function wrapTurn(result: AssistantTurnResult): AssistantTurnResult {
  const flow = result.session?.flow ?? 'idle';
  const suggestions = result.reply.suggestions?.length
    ? result.reply.suggestions
    : [...suggestionsForFlow(flow === 'idle' ? 'idle' : flow)];

  return {
    ...result,
    reply: {
      ...result.reply,
      content: sanitizeUserFacingText(result.reply.content),
      source: AI_SOURCE,
      rich: result.reply.rich,
      suggestions,
    },
  };
}

export function paymentPrompt(ctx: { languageCode?: string }): string {
  return t(langKeyFromCode(ctx.languageCode), 'paymentChoose');
}
